import type { AxiosResponse } from 'axios';

function parseTotalCountHeader(headers: AxiosResponse<unknown>['headers']): number | null {
  const raw = headers['x-total-count'] ?? headers['X-Total-Count'];
  const n = parseInt(String(raw ?? ''), 10);
  return Number.isNaN(n) ? null : n;
}

function parseNonNegativeInt(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return Math.floor(value);
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const n = parseInt(value, 10);
    return Number.isNaN(n) || n < 0 ? undefined : n;
  }
  return undefined;
}

export interface ParsedSieveListPayload<T> {
  rows: T[];
  totalCount: number;
  /** From paged OpenAPI envelopes (e.g. `PagedResultOfCrossUserListItemDto`). */
  serverTotalPages?: number;
  serverPageNumber?: number;
}

/**
 * Normalizes User Management list responses: OpenAPI does not document response schemas, so the API may return
 * a bare array, `{ items, totalCount }`, or `{ data: { items, totalCount } }` (common Raptor patterns).
 */
export function parseSieveListPayload<T>(
  data: unknown,
  headers: AxiosResponse<unknown>['headers'],
  fallbackLength: number
): ParsedSieveListPayload<T> {
  const headerTotal = parseTotalCountHeader(headers);

  if (data instanceof Blob) {
    throw new Error('Expected JSON list payload, received blob');
  }

  if (Array.isArray(data)) {
    const totalCount = headerTotal !== null && headerTotal > 0 ? headerTotal : data.length;
    return { rows: data as T[], totalCount };
  }

  if (data !== null && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    const inner = o.data;

    if (Array.isArray(inner)) {
      const totalCount = headerTotal !== null && headerTotal > 0 ? headerTotal : inner.length;
      return { rows: inner as T[], totalCount };
    }

    const innerObj = inner !== null && typeof inner === 'object' ? (inner as Record<string, unknown>) : null;

    /** Some UM list endpoints return domain-specific array keys (e.g. `approvalRequestDtos`) instead of `items`. */
    const items = (o.rows ??
      o.Rows ??
      o.items ??
      o.approvalRequestDtos ??
      o.ApprovalRequestDtos ??
      o.auditLogEntryDtos ??
      o.AuditLogEntryDtos ??
      o.roleDtos ??
      o.RoleDtos ??
      o.permissionSnapshotDtos ??
      o.PermissionSnapshotDtos ??
      innerObj?.rows ??
      innerObj?.Rows ??
      innerObj?.items ??
      innerObj?.approvalRequestDtos ??
      innerObj?.ApprovalRequestDtos ??
      innerObj?.auditLogEntryDtos ??
      innerObj?.AuditLogEntryDtos ??
      innerObj?.roleDtos ??
      innerObj?.RoleDtos) as unknown;
    if (Array.isArray(items)) {
      const tcRaw = o.totalCount ?? o.totalRecords ?? o.count ?? innerObj?.totalCount ?? innerObj?.totalRecords;
      const tc = typeof tcRaw === 'number' ? tcRaw : parseInt(String(tcRaw ?? ''), 10);
      const totalCount = Number.isFinite(tc) && tc >= 0 ? tc : headerTotal !== null && headerTotal > 0 ? headerTotal : items.length;
      const serverTotalPages = parseNonNegativeInt(o.totalPages ?? innerObj?.totalPages);
      const serverPageNumber = parseNonNegativeInt(o.pageNumber ?? innerObj?.pageNumber);
      return {
        rows: items as T[],
        totalCount,
        serverTotalPages,
        serverPageNumber
      };
    }
  }

  return { rows: [], totalCount: headerTotal ?? fallbackLength };
}
